// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { NonIndexRouteObject } from "react-router-dom";
import { View } from "./view/Index";
import React from "react";
import { isIncludedRole } from "./utils/utils";

// icons
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PortraitIcon from "@mui/icons-material/Portrait";
import BarChartIcon from "@mui/icons-material/BarChart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";

import { Role } from "@utils/types";

export interface RouteObjectWithRole extends NonIndexRouteObject {
  allowRoles: Role[];
  icon:
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined;
  text: string;
  children?: RouteObjectWithRole[];
  bottomNav?: boolean;
}

interface RouteDetail {
  path: string;
  allowRoles: Role[];
  icon:
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined;
  text: string;
  bottomNav?: boolean;
}

export const routes: RouteObjectWithRole[] = [
  {
    path: "job-description",
    text: "Job Description",
    icon: React.createElement(AssignmentIcon),
    element: React.createElement(View.JobDescription),
    allowRoles: [
      Role.EMPLOYEE_ROLE,
      Role.HR_ROLE,
      Role.LEAD_ROLE,
      Role.HIRING_MANAGER_ROLE,
    ],
  },
  {
    path: "job-requisition",
    text: "Job Requisition",
    icon: React.createElement(PersonSearchIcon),
    element: React.createElement(View.JobRequisition),
    allowRoles: [Role.HR_ROLE, Role.LEAD_ROLE, Role.HIRING_MANAGER_ROLE],
  },
  {
    path: "/vacancy",
    text: "Vacancy",
    icon: React.createElement(CardTravelIcon),
    element: React.createElement(View.Vacancy),
    allowRoles: [Role.HR_ROLE, Role.MARKETING_ROLE, Role.HIRING_MANAGER_ROLE],
  },
  {
    path: "/candidate",
    text: "Candidate Applications",
    icon: React.createElement(PortraitIcon),
    element: React.createElement(View.Candidate),
    allowRoles: [Role.HR_ROLE, Role.HIRING_MANAGER_ROLE],
  },
  {
    path: "/applicant-overview",
    text: "Applicant Overview",
    icon: React.createElement(BarChartIcon),
    element: React.createElement(View.ApplicantOverview),
    allowRoles: [Role.EMPLOYEE_ROLE, Role.HIRING_MANAGER_ROLE, Role.HR_ROLE],
  },
  {
    path: "/interview",
    text: "Interview",
    icon: React.createElement(FactCheckIcon),
    element: React.createElement(View.InterviewView),
    allowRoles: [Role.EMPLOYEE_ROLE, Role.HIRING_MANAGER_ROLE, Role.HR_ROLE, Role.ADMIN_ROLE],
  },
  {
    path: "referral-vacancy",
    text: "Referral Vacancy",
    icon: React.createElement(WorkOutlineIcon),
    element: React.createElement(View.ReferralVacancy),
    allowRoles: [
      Role.EMPLOYEE_ROLE,
      Role.HR_ROLE,
      Role.LEAD_ROLE,
      Role.HIRING_MANAGER_ROLE,
    ],
  },
  {
    path: "/offer-letters",
    text: "Offer Letters",
    icon: React.createElement(MailOutlineIcon),
    element: React.createElement(View.OfferLetters),
    allowRoles: [Role.HR_ROLE],
  },
];

export const getActiveRoutesV2 = (
  routes: RouteObjectWithRole[] | undefined,
  roles: Role[],
): RouteObjectWithRole[] => {
  if (!routes) return [];
  var routesObj: RouteObjectWithRole[] = [];
  routes.forEach((routeObj) => {
    if (isIncludedRole(roles, routeObj.allowRoles)) {
      routesObj.push({
        ...routeObj,
        children: getActiveRoutesV2(routeObj.children, roles),
      });
    }
  });

  return routesObj;
};

export const getActiveRouteDetails = (roles: Role[]): RouteDetail[] => {
  var routesObj: RouteDetail[] = [];
  routes.forEach((routeObj) => {
    if (isIncludedRole(roles, routeObj.allowRoles)) {
      routesObj.push({
        path: routeObj.path ? routeObj.path : "",
        ...routeObj,
      });
    }
  });
  return routesObj;
};
